// Notifications SMS — /sms-notifications. The SMS a tenant's parcel
// recipients receive about their tracking (delay detected, moving again,
// delivered), the sender they see (Unanimity, or the company once OVH
// validated its documents), a test send and the latest sends
// (Backend docs/customer-sms.md). No phone number to provide: SMS leave from
// Unanimity's OVH account. API: GET/PUT /settings, POST/DELETE /sender,
// POST /sender/refresh, POST /test, GET /messages (customerSms/api.js).

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { BellRing, Building2, OctagonX, Send, Smartphone } from 'lucide-react';
import SenderSection from './SenderSection';
import NotificationsSection from './NotificationsSection';
import { HistoryCard, TestCard } from './ActivitySection';
import SmsPreview from './SmsPreview';
import { EVENTS, fail, get, put, renderSms } from './api';
import { AttentionCard, SaveBar, kitLabels, scrollToId } from '../ChannelsKit';
import {
  Banner, Card, Kpi, PageHeader, Toast,
} from '../../design/ui';
import './CustomerSms.css';

const draftFrom = (view) => ({
  sender_mode: view.sender_mode,
  language: view.language,
  events: Object.fromEntries(EVENTS.map((event) => {
    const { enabled, text, only_after_problem: onlyAfter } = view.events[event];
    return [event, { enabled, text: text || null, ...(event === 'parcel_delivered' ? { only_after_problem: onlyAfter !== false } : {}) }];
  })),
});

// « À vérifier »: what keeps SMS from leaving, or makes them fail.
export const smsChecks = (view) => {
  const checks = [];
  if (!view.ovh_configured) checks.push({ key: 'notConfigured', tone: 'danger' });
  if (view.sender.effective && !view.sender.effective.ready) checks.push({ key: 'platformInactive', tone: 'danger' });
  const own = view.sender.own;
  if (view.sender_mode === 'own' && own && ['refused', 'disabled'].includes(own.status)) checks.push({ key: `own_${own.status}`, tone: 'danger' });
  if (!view.parcel_monitoring) checks.push({ key: 'noMonitoring', tone: 'warning' });
  if (view.ovh_configured && view.parcel_monitoring && !EVENTS.some((event) => view.events[event]?.enabled)) {
    checks.push({ key: 'noneEnabled', tone: 'warning' });
  }
  if ((view.usage?.failed || 0) > 0) checks.push({ key: 'failed', tone: 'warning', count: view.usage.failed });
  return checks;
};

const CustomerSms = () => {
  const { t, i18n } = useTranslation();
  const lang = i18n.language || 'fr';
  const navigate = useNavigate();
  const kit = kitLabels(t);
  const [view, setView] = useState(null);
  const [baseline, setBaseline] = useState(null);
  const [draft, setDraft] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loadError, setLoadError] = useState('');
  const [saving, setSaving] = useState(false);
  const [feedback, setFeedback] = useState(null);
  const [focusEvent, setFocusEvent] = useState('parcel_problem');
  const [historyFilter, setHistoryFilter] = useState('all');

  const applyView = useCallback((next, { keepDraft = false } = {}) => {
    setView(next);
    const fresh = draftFrom(next);
    setBaseline(fresh);
    // Sender actions answer the whole view: keep unsaved texts/toggles.
    setDraft((prev) => (keepDraft && prev ? { ...prev, sender_mode: fresh.sender_mode } : fresh));
  }, []);

  const loadMessages = useCallback(async () => {
    try {
      const res = await get('/messages', { limit: 30 });
      setMessages(res.data.items || []);
    } catch (e) {
      setMessages([]);
    }
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const res = await get('/settings');
        applyView(res.data);
      } catch (err) {
        setLoadError(fail(err, t('customerSms.loadError', 'Impossible de charger les réglages SMS.')));
      }
    })();
    loadMessages();
  }, [applyView, loadMessages, t]);

  useEffect(() => {
    if (!feedback || feedback.tone === 'danger') return undefined;
    const timer = setTimeout(() => setFeedback(null), 5000);
    return () => clearTimeout(timer);
  }, [feedback]);

  const dirty = useMemo(() => Boolean(draft && baseline && JSON.stringify(draft) !== JSON.stringify(baseline)), [draft, baseline]);

  const save = async () => {
    setSaving(true);
    setFeedback(null);
    try {
      // A text equal to the default is no custom text (it follows the language).
      const events = Object.fromEntries(EVENTS.map((event) => {
        const item = draft.events[event];
        const defaultText = view.events[event].default_text?.[draft.language];
        const text = item.text && item.text.trim() && item.text.trim() !== defaultText ? item.text.trim() : null;
        return [event, { ...item, text }];
      }));
      const res = await put('/settings', { ...draft, events });
      applyView(res.data);
      setFeedback({ tone: 'success', text: t('customerSms.saved', 'Réglages enregistrés.') });
    } catch (err) {
      setFeedback({ tone: 'danger', text: fail(err, t('customerSms.saveError', "Les réglages n'ont pas pu être enregistrés.")) });
    } finally {
      setSaving(false);
    }
  };

  const header = (actions) => (
    <PageHeader
      title={t('customerSms.title', 'Notifications SMS')}
      subtitle={t('customerSms.subtitle', 'Prévenez les destinataires de vos colis par SMS : retard détecté, colis reparti, colis livré.')}
      actions={actions}
    />
  );

  if (loadError) {
    return (
      <div className="u-page u-page--narrow sms-v2">
        {header(null)}
        <div className="u-well"><Banner tone="danger">{loadError}</Banner></div>
      </div>
    );
  }
  if (!view || !draft) {
    return (
      <div className="u-page u-page--narrow sms-v2" aria-busy="true" aria-label={t('customerSms.loading', 'Chargement…')}>
        {header(null)}
        <div className="u-well">
          <div className="u-kpi-row">{[0, 1, 2, 3].map((i) => <span key={i} className="u-skeleton sms-v2__kpi-skeleton" />)}</div>
          <span className="u-skeleton sms-v2__card-skeleton" />
        </div>
      </div>
    );
  }

  const own = view.sender.own;
  const platformName = view.sender.platform.name;
  const previewSender = draft.sender_mode === 'own' && own?.status === 'active' ? own.name : platformName;
  const focusConfig = draft.events[focusEvent];
  const focusDefault = view.events[focusEvent].default_text?.[draft.language] || '';
  const previewText = renderSms(focusConfig.text?.trim() ? focusConfig.text : focusDefault, view.samples);
  const usage = view.usage || {};
  const savedEnabled = EVENTS.filter((event) => view.events[event]?.enabled);
  const effective = view.sender.effective || { name: platformName, ready: true };

  const showHistory = (status) => {
    setHistoryFilter(status);
    scrollToId('sms-history');
  };

  const checkText = (check) => {
    switch (check.key) {
    case 'notConfigured':
      return { title: t('customerSms.banner.notConfigured', "L'envoi de SMS n'est pas encore configuré sur la plateforme : contactez le support.") };
    case 'platformInactive':
      return {
        title: t('customerSms.banner.platformInactive', "L'expéditeur « {{name}} » n'est pas encore actif chez OVH : les SMS ne partent pas encore.", { name: platformName }),
      };
    case 'own_refused':
      return {
        title: t('channelsUi.sms.checkRefused', 'OVH a refusé l’expéditeur « {{name}} »', { name: own.name }),
        detail: own.moderator_comment || t('customerSms.sender.refusedHint', 'Corrigez le document concerné et envoyez une nouvelle demande.'),
        action: { label: t('channelsUi.sms.openSender', 'Voir l’expéditeur'), onClick: () => scrollToId('sms-sender') },
      };
    case 'own_disabled':
      return {
        title: t('customerSms.sender.disabledHint', "OVH a désactivé cet expéditeur. Envoyez une nouvelle demande ou revenez à « {{name}} ».", { name: platformName }),
        action: { label: t('channelsUi.sms.openSender', 'Voir l’expéditeur'), onClick: () => scrollToId('sms-sender') },
      };
    case 'noMonitoring':
      return {
        title: t('channelsUi.sms.checkNoMonitoring', 'La surveillance colis est désactivée : aucun SMS ne part'),
        detail: t('customerSms.banner.noMonitoring', 'Les SMS partent pour les colis suivis par la surveillance colis, désactivée sur votre compte : aucun SMS ne sera envoyé.'),
        action: { label: t('customerSms.banner.openMonitoring', 'Ouvrir la surveillance colis'), onClick: () => navigate('/parcel-monitor') },
      };
    case 'noneEnabled':
      return {
        title: t('channelsUi.sms.checkNoneEnabled', 'Aucune notification activée : vos destinataires ne reçoivent pas de SMS'),
        detail: t('channelsUi.sms.checkNoneEnabledDetail', 'Activez au moins « Colis en retard » : c’est le SMS qui évite le plus d’appels.'),
        action: { label: t('channelsUi.sms.openEvents', 'Choisir les notifications'), onClick: () => scrollToId('sms-events') },
      };
    default:
      return {
        title: t('channelsUi.sms.checkFailed', '{{count}} SMS refusés par l’opérateur ce mois-ci', { count: check.count }),
        detail: t('channelsUi.sms.checkFailedDetail', 'Numéro invalide ou injoignable : le détail est dans les derniers envois.'),
        action: { label: t('channelsUi.sms.openFailed', 'Voir les envois'), onClick: () => showHistory(messages.some((m) => m.status === 'failed') ? 'failed' : 'all') },
      };
    }
  };

  const senderContext = (() => {
    if (view.sender_mode === 'own' && own?.status === 'active') return t('channelsUi.sms.senderOwn', 'votre entreprise, validée par OVH');
    if (view.sender_mode === 'own' && own?.status === 'pending') return t('channelsUi.sms.senderPending', '« {{name}} » en validation chez OVH', { name: own.name });
    return t('channelsUi.sms.senderPlatform', 'expéditeur de la plateforme');
  })();

  return (
    <div className="u-page u-page--narrow sms-v2">
      {header(view.can_edit ? (
        <button type="button" className="u-btn u-btn--secondary" onClick={() => scrollToId('sms-test')}>
          <Send aria-hidden="true" />
          {t('channelsUi.sms.sendTest', 'Envoyer un essai')}
        </button>
      ) : null)}

      <div className="u-well">
        <AttentionCard
          items={smsChecks(view).map((check) => ({ ...check, ...checkText(check) }))}
          title={kit.attention}
          criticalLabel={kit.critical}
          watchLabel={kit.watch}
        />

        <div className="u-kpi-row">
          <Kpi
            icon={Send}
            label={t('channelsUi.sms.kpiSent', 'SMS envoyés')}
            value={(usage.sent || 0).toLocaleString(lang)}
            context={t('channelsUi.sms.kpiSentContext', 'ce mois-ci · programmés compris')}
          />
          <Kpi
            icon={OctagonX}
            tone={usage.failed > 0 ? 'danger' : 'neutral'}
            label={t('channelsUi.sms.kpiFailed', 'Refusés par l’opérateur')}
            value={(usage.failed || 0).toLocaleString(lang)}
            context={t('channelsUi.sms.kpiFailedContext', 'ce mois-ci · {{skipped}} non envoyés', { skipped: (usage.skipped || 0).toLocaleString(lang) })}
            contextTone={usage.failed > 0 ? 'danger' : undefined}
            onClick={messages.some((m) => m.status === 'failed') ? () => showHistory('failed') : undefined}
            active={historyFilter === 'failed'}
          />
          <Kpi
            icon={BellRing}
            tone="info"
            label={t('channelsUi.sms.kpiEvents', 'Notifications actives')}
            value={savedEnabled.length}
            unit="/ 3"
            meter={{ value: savedEnabled.length, max: EVENTS.length, label: t('channelsUi.sms.kpiEvents', 'Notifications actives') }}
            context={savedEnabled.length
              ? savedEnabled.map((event) => t(`customerSms.events.${event}.title`, event)).join(' · ')
              : t('channelsUi.sms.kpiEventsNone', 'aucune : aucun SMS ne part')}
          />
          <Kpi
            icon={Building2}
            tone={effective.ready ? 'success' : 'danger'}
            label={t('channelsUi.sms.kpiSender', 'Expéditeur affiché')}
            value={effective.name}
            context={senderContext}
          />
        </div>

        <div className="sms-v2__layout">
          <div className="sms-v2__column">
            <NotificationsSection view={view} draft={draft} focusEvent={focusEvent} setFocusEvent={setFocusEvent}
              onDraft={(patch) => setDraft((prev) => ({ ...prev, ...patch }))} />
            <SenderSection view={view} senderMode={draft.sender_mode} lang={lang} setFeedback={setFeedback}
              onModeChange={(mode) => setDraft((prev) => ({ ...prev, sender_mode: mode }))}
              onViewChange={(next) => { applyView(next, { keepDraft: true }); }} />
            {view.can_edit && <TestCard view={view} onSent={loadMessages} />}
            {view.can_edit && (
              <SaveBar
                dirty={dirty}
                saving={saving}
                canSave={dirty}
                onSave={save}
                onReset={() => setDraft(baseline)}
                labels={{ ...kit.save, save: t('customerSms.save', 'Enregistrer'), saving: t('customerSms.saving', 'Enregistrement…') }}
              />
            )}
          </div>
          <aside className="sms-v2__preview">
            <Card icon={Smartphone} title={kit.preview} description={t('channelsUi.sms.previewDesc', 'Le SMS de la notification survolée, tel que le destinataire le reçoit.')}>
              <SmsPreview sender={previewSender} text={previewText} enabled={focusConfig.enabled}
                eventLabel={t(`customerSms.events.${focusEvent}.title`, focusEvent)} />
            </Card>
          </aside>
        </div>

        <HistoryCard messages={messages} lang={lang} filter={historyFilter} onFilter={setHistoryFilter} />
      </div>

      {feedback && (
        <Toast tone={feedback.tone === 'danger' ? 'danger' : 'success'} onClose={() => setFeedback(null)} closeLabel={kit.close}>
          {feedback.text}
        </Toast>
      )}
    </div>
  );
};

export default CustomerSms;
